import {
  Button,
  colorTokens,
  Dialog,
  H2,
  mediaHelper,
  MOBILE_MAX,
  Modal,
  P,
  paletteHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { DynamicContent } from '../../app-kit/components/DynamicContent/DynamicContent';
import { DynamicContentsItem } from '../../app-kit/components/DynamicContent/types';
import { convertHexStringToRGBA } from '../../app-kit/helpers/convertHexStringToRGBA';
import { modalCloseButton } from '../../app-kit/helpers/modalCloseButton';

export const MODAL_MAX_WIDTH = `450px`;
export const MODAL_PADDING = '5rem';
export const SEARCH_DIALOG_PADDING = '3rem';
interface SearchModalProps {
  opened: boolean;
  title?: string;
  subtitle?: string;
  onCancel: () => void;
  onConfirm?: () => void;
  modalClassName?: string;
  disableConfirm?: boolean;
  footerContent?: DynamicContentsItem[];
  showDialogBodyOverlay?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  renderCancelButton?: boolean;
}

const SearchDialog = styled(Dialog)`
  .header {
    background: ${paletteHelper('backgroundPrimaryBase')};
  }

  .body {
    margin-top: 0;
  }

  .closeButton {
    ${modalCloseButton};
  }
`;

const Subtitle = styled(P)`
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('twoExtraSmall')};
`;

const FakeSubmitButton = styled.button`
  /* Fake submit button to enable keyboard to submit form */
  display: none;
`;

const SearchBodyFooterContent = styled.div`
  ${typographyHelper('body02')};
  margin-top: ${spacingHelper('large')};

  a {
    ${typographyHelper('body02')};
  }

  span {
    ${typographyHelper('body02')};
    color: ${paletteHelper('textSecondary')};
  }
`;

const StyledModal = styled(Modal)`
  .searchModal {
    max-height: 800px !important; /* Using !important to ensure 800px isn't overwritten by React Modal library's inline max-height of 100vh */
    width: 100%;
    display: flex;
    justify-content: center;
    max-width: ${MODAL_MAX_WIDTH};
    outline: none;

    ${mediaHelper('mobile')} {
      max-height: unset !important; /* Using !important to ensure max-height isn't overwritten by the !important of desktop max-height */
      max-width: unset;
    }
  }
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacingHelper('extraSmall')};
  padding: ${spacingHelper('extraSmall')};

  ${mediaHelper('mobile')} {
    justify-content: space-between;
  }
`;

const StyledButton = styled(Button)`
  ${mediaHelper('mobile')} {
    flex-grow: 1;
  }
`;

export const SearchModal: FunctionComponent<PropsWithChildren<SearchModalProps>> = ({
  opened,
  onCancel,
  onConfirm,
  title,
  subtitle,
  modalClassName = 'searchModal',
  disableConfirm = false,
  footerContent,
  showDialogBodyOverlay = true,
  header,
  footer,
  renderCancelButton = false,
  children,
}) => {
  const backgroundColor = convertHexStringToRGBA(colorTokens.backgroundOverlayStatic.light, 0.3);

  return (
    <StyledModal
      opened={opened}
      getAppElement={() => '#argonaut-wrapper'}
      onRequestClose={onCancel}
      reactModalProps={{
        role: 'presentation',
        className: modalClassName,
        style: {
          overlay: {
            backgroundColor,
          },
        },
      }}
    >
      <SearchDialog
        gap="0.75rem"
        autoFullScreen
        fullScreenMediaQuery={`(max-width: ${MOBILE_MAX}px)`}
        onClose={renderCancelButton ? onCancel : undefined}
        header={
          header ?? (
            <div>
              <H2 variant="title03">{title}</H2>
              {subtitle && <Subtitle variant="body01">{subtitle}</Subtitle>}
            </div>
          )
        }
        footer={
          footer ?? (
            <FooterWrapper>
              <StyledButton variant="outline-secondary" sizeVariant="medium" onClick={onCancel}>
                Cancel
              </StyledButton>

              <StyledButton
                variant="primary"
                sizeVariant="medium"
                onClick={onConfirm}
                disabled={disableConfirm}
              >
                I&apos;m done
              </StyledButton>
            </FooterWrapper>
          )
        }
        showDialogBodyOverlay={showDialogBodyOverlay}
        data-testid="search-dialog-test-id"
      >
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (onConfirm) onConfirm();
          }}
        >
          {children}
          <FakeSubmitButton type="submit" />
        </form>

        {footerContent && footerContent?.length > 0 && (
          <SearchBodyFooterContent>
            <DynamicContent dynamicContents={footerContent} />
          </SearchBodyFooterContent>
        )}
      </SearchDialog>
    </StyledModal>
  );
};
